import { useMemo, useState } from "react";
import { Polygon } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import ProspectLayer from "@/components/shop/ProspectLayer";
import ProspectCard from "@/components/shop/ProspectCard";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CATS, STATUSES, STATUS_COLOR, CAT_LETTER, fmtMoney, scoreColor } from "@/lib/prospects";
import { toast } from "sonner";

function OppStat({ label, value, className = "" }) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-3 text-center">
      <div className={`text-xl font-heading font-extrabold ${className}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function MiniStat({ label, value, dot }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-2">
      <div className="text-base font-bold flex items-center justify-center gap-1">{dot && <span className="w-2 h-2 rounded-full" style={{ background: dot }} />}{value}</div>
      <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</div>
    </div>
  );
}

// Shared territory prospect board — identical experience for the shop "My Service
// Area" and the admin Zone dashboard. Data (same shared records) is passed in;
// onUpdate persists status/notes back to the shared prospect.
export default function ProspectBoard({
  title, prospects = [], advertisers = [], opportunity = {}, osmStatus, syncing,
  zonePolygons = [], boundary = [], center, onUpdate, onSync, syncingNow = false, onSaveToRider, onRemoveFromRider,
}) {
  const [layers, setLayers] = useState(() => Object.fromEntries(CATS.map((c) => [c.key, true])));
  const [showAdv, setShowAdv] = useState(true);
  const [statusSel, setStatusSel] = useState(() => new Set(STATUSES));
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingRider, setSavingRider] = useState(false);
  const [removingRider, setRemovingRider] = useState(false);

  const filtered = useMemo(
    () => prospects.filter((p) => layers[p.category] && statusSel.has(p.status)),
    [prospects, layers, statusSel]
  );

  const counts = useMemo(() => {
    const c = {};
    CATS.forEach((x) => { c[x.key] = 0; });
    STATUSES.forEach((s) => { c[s] = 0; });
    filtered.forEach((p) => { c[p.category] = (c[p.category] || 0) + 1; c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [filtered]);

  const toggleStatus = (s) => setStatusSel((prev) => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(sel.id, { status: sel.status, notes: sel.notes });
      setSel(null);
      toast.success("Prospect updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update prospect"); }
    finally { setSaving(false); }
  };

  const saveRider = async (p, photos) => {
    setSavingRider(true);
    try {
      const updated = await onSaveToRider(p, photos);
      if (updated) setSel((s) => (s ? { ...s, ...updated } : s));
      toast.success("Added to the Rider map");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to the Rider map"); }
    finally { setSavingRider(false); }
  };

  const removeRider = async (p) => {
    setRemovingRider(true);
    try {
      const updated = await onRemoveFromRider(p);
      if (updated) setSel((s) => (s ? { ...s, ...updated, rider_saved: false } : s));
      toast.success("Removed from the Rider map");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove from the Rider map"); }
    finally { setRemovingRider(false); }
  };

  const allPts = zonePolygons.flat();
  const mapCenter = center || (allPts[0] ? [allPts[0][0], allPts[0][1]] : [41.75, -71.3]);

  return (
    <div className="space-y-4" data-testid="prospect-board">
      {/* Opportunity summary */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="territory-summary">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-heading font-bold">{title || "Territory"}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {syncing ? <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Syncing prospects…</span> : `${opportunity.prospectCount ?? prospects.length} prospects in territory`}
            {onSync && <Button size="sm" variant="outline" className="h-7" disabled={syncingNow} onClick={onSync} data-testid="prospect-sync-btn">{syncingNow ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Sync</Button>}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <OppStat label="Prospects" value={opportunity.prospectCount ?? prospects.length} className="text-primary" />
          <OppStat label="Opportunity Score" value={`${opportunity.opportunityScore ?? 0}/100`} className={scoreColor(opportunity.opportunityScore ?? 0)} />
          <OppStat label="Est. Monthly Rev" value={`${fmtMoney(opportunity.projectedMonthly)}/mo`} className="text-emerald-400" />
          <OppStat label="Est. Annual Rev" value={fmtMoney(opportunity.projectedAnnual)} className="text-emerald-400" />
        </div>
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
          <MiniStat label="Shown" value={filtered.length} />
          {CATS.map((c) => <MiniStat key={c.key} label={c.label} value={counts[c.key]} />)}
        </div>
        <div className="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
          {STATUSES.map((s) => <MiniStat key={s} label={s} value={counts[s]} dot={STATUS_COLOR[s]} />)}
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground">Estimated revenue is a heuristic projection based on the prospect mix in this territory.</div>
        {osmStatus === "unavailable" && !syncing && (
          <div className="mt-1 text-[11px] text-amber-500" data-testid="prospects-osm-warning">Live prospect data is temporarily unavailable — showing previously synced prospects.</div>
        )}
      </div>

      {/* Layer + status filters */}
      <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid="prospect-filters">
        <div className="flex flex-wrap gap-1.5">
          {CATS.map((c) => (
            <button key={c.key} type="button" onClick={() => setLayers((l) => ({ ...l, [c.key]: !l[c.key] }))}
              data-testid={`layer-toggle-${c.key}`}
              className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${layers[c.key] ? "bg-primary/15 border-primary/40 text-foreground" : "bg-transparent border-border text-muted-foreground"}`}>
              {c.label} <span className="opacity-60">{counts[c.key]}</span>
            </button>
          ))}
          <button type="button" onClick={() => setShowAdv((v) => !v)} data-testid="layer-toggle-advertisers"
            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${showAdv ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-transparent border-border text-muted-foreground"}`}>
            Advertisers <span className="opacity-60">{advertisers.length}</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
          {STATUSES.map((s) => (
            <button key={s} type="button" onClick={() => toggleStatus(s)} data-testid={`status-filter-${s.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${statusSel.has(s) ? "bg-card border-border text-foreground" : "bg-transparent border-border/50 text-muted-foreground opacity-60"}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />{s}
            </button>
          ))}
        </div>
      </div>

      <LeafletMap height={460} center={mapCenter}>
        {boundary?.length >= 3 && <Polygon positions={boundary} pathOptions={{ color: "#64748B", weight: 2, fillOpacity: 0.04, dashArray: "6" }} />}
        {zonePolygons.map((poly, i) => (poly?.length >= 3) && <Polygon key={i} positions={poly} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.12, weight: 2 }} />)}
        {allPts.length >= 2 && <FitBounds bounds={allPts} />}
        <ProspectLayer prospects={filtered} advertisers={advertisers} showAdvertisers={showAdv} statusColor={STATUS_COLOR} catLetter={CAT_LETTER} onSelect={(p) => setSel({ ...p })} />
      </LeafletMap>

      <ProspectCard sel={sel} setSel={setSel} onSave={save} saving={saving} onSaveToRider={onSaveToRider ? saveRider : undefined} savingRider={savingRider} onRemoveFromRider={onRemoveFromRider ? removeRider : undefined} removingRider={removingRider} />
    </div>
  );
}
